import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getPsActiveSeason, getPsLeaderboard, getPsPool } from "../api/mlbPlayoff";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

const MLB_ABBREVS = {
  "Arizona Diamondbacks": "ARI", "Atlanta Braves": "ATL", "Baltimore Orioles": "BAL",
  "Boston Red Sox": "BOS", "Chicago Cubs": "CHC", "Chicago White Sox": "CWS",
  "Cincinnati Reds": "CIN", "Cleveland Guardians": "CLE", "Colorado Rockies": "COL",
  "Detroit Tigers": "DET", "Houston Astros": "HOU", "Kansas City Royals": "KC",
  "Los Angeles Angels": "LAA", "Los Angeles Dodgers": "LAD", "Miami Marlins": "MIA",
  "Milwaukee Brewers": "MIL", "Minnesota Twins": "MIN", "New York Mets": "NYM",
  "New York Yankees": "NYY", "Oakland Athletics": "OAK", "Athletics": "ATH",
  "Sacramento Athletics": "ATH", "Philadelphia Phillies": "PHI", "Pittsburgh Pirates": "PIT",
  "San Diego Padres": "SD", "San Francisco Giants": "SF", "Seattle Mariners": "SEA",
  "St. Louis Cardinals": "STL", "Tampa Bay Rays": "TB", "Texas Rangers": "TEX",
  "Toronto Blue Jays": "TOR", "Washington Nationals": "WSH",
};

function teamAbbr(name) {
  if (!name) return "—";
  if (MLB_ABBREVS[name]) return MLB_ABBREVS[name];
  if (name.length <= 4) return name;
  return name.substring(0, 3).toUpperCase();
}

export default function MlbPlayoffRostersView() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);

  const username = user?.username || "Player";

  const [leaderboard, setLeaderboard] = useState([]);
  const [poolName,    setPoolName]    = useState("");
  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError,   setPageError]   = useState("");

  useEffect(() => {
    async function loadPage() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      try {
        const season = await getPsActiveSeason();
        const [lbData, poolData] = await Promise.all([
          getPsLeaderboard(season.id, numericPoolId),
          getPsPool(season.id, numericPoolId),
        ]);
        setLeaderboard(lbData.leaderboard || []);
        setPoolName(poolData.name || "");
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    loadPage();
  }, [numericPoolId]);

  function isMyEntry(entry) {
    return (entry.username || "").trim().toLowerCase() === username.trim().toLowerCase();
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading rosters...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="MLB PLAYOFF CHALLENGE"
        onBack={() => navigate(`/mlb-playoff/current/${numericPoolId}`)}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />{pageError}
          </Alert>
        )}

        <HStack mb={6} spacing={3}>
          <Button
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/mlb-playoff/current/${numericPoolId}`)}
            _hover={{ bg: "gray.100" }}
          >
            Leaderboard View
          </Button>
        </HStack>

        {poolName && (
          <Heading size="md" mb={5}>{poolName} — All Rosters</Heading>
        )}

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5}>
          {leaderboard.map((entry) => {
            const isMe = isMyEntry(entry);
            return (
              <Box
                key={entry.entry_id}
                bg={isMe ? "yellow.50" : "white"}
                color="black"
                border={`3px solid ${isMe ? "#b7791f" : TOP_BAR_BLUE}`}
                borderRadius="lg"
                boxShadow={isMe ? "0 0 0 2px #b7791f" : "lg"}
                p={4}
              >
                <VStack align="stretch" spacing={2}>
                  <HStack spacing={2}>
                    <Badge
                      bg={TOP_BAR_BLUE}
                      color="white"
                      fontSize="xs"
                      px={2}
                      borderRadius="sm"
                    >
                      #{entry.rank}
                    </Badge>
                  </HStack>

                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    color={TOP_BAR_BLUE}
                    noOfLines={1}
                    title={entry.team_name}
                  >
                    {entry.team_name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">{entry.username}</Text>

                  <Table size="xs" variant="simple">
                    <Thead>
                      <Tr>
                        <Th px={1} fontSize="10px">Player</Th>
                        <Th px={1} fontSize="10px">Tm</Th>
                        <Th px={1} fontSize="10px" isNumeric>TB</Th>
                        <Th px={1} fontSize="10px" isNumeric>AVG</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(entry.players || []).map((p) => {
                        const avg = p.at_bats > 0 ? (p.hits / p.at_bats).toFixed(3) : "—";
                        return (
                          <Tr key={p.player_id} opacity={p.is_eliminated ? 0.5 : 1}>
                            <Td px={1} fontSize="xs">
                              <Text
                                textDecoration={p.is_eliminated ? "line-through" : "none"}
                                color={p.is_eliminated ? "gray.400" : "inherit"}
                              >
                                {p.name}
                              </Text>
                            </Td>
                            <Td px={1} fontSize="xs">{teamAbbr(p.team)}</Td>
                            <Td px={1} fontSize="xs" isNumeric fontWeight="bold">{p.total_bases}</Td>
                            <Td px={1} fontSize="xs" isNumeric>{avg}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                    <Tfoot>
                      <Tr bg="gray.100">
                        <Td px={1} fontSize="xs" fontWeight="bold" colSpan={2}>Total</Td>
                        <Td px={1} fontSize="xs" isNumeric fontWeight="bold" color={TOP_BAR_BLUE}>
                          {entry.total_bases}
                        </Td>
                        <Td px={1} fontSize="xs" isNumeric fontWeight="bold">
                          {(() => {
                            const players = entry.players || [];
                            const totalH  = players.reduce((s, p) => s + (p.hits || 0), 0);
                            const totalAB = players.reduce((s, p) => s + (p.at_bats || 0), 0);
                            return totalAB > 0 ? (totalH / totalAB).toFixed(3) : "—";
                          })()}
                        </Td>
                      </Tr>
                    </Tfoot>
                  </Table>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
